import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUpRight01Icon, CheckListIcon, CheckmarkCircle01Icon, EuroIcon, HandshakeIcon, LockIcon } from "@hugeicons-pro/core-bulk-rounded";

import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "@/components/layout/page-header";
import { KpiTile } from "@/components/dashboard/kpi-tile";
import { BreakdownTable, type Column } from "@/components/dashboard/breakdown-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import { FadeIn } from "@/components/ui/motion";
import { Skeleton } from "@/components/ui/skeleton";
import { useMoney } from "@/lib/use-money";
import { relativeDate } from "@/lib/format";
import {
  getManagedCollaborations, managedStats, MANAGED_STATUS, managedCollabUrl,
  type ManagedCollab,
} from "@/lib/api/managed-collabs";

export function Collaborations() {
  const m = useMoney();
  const q = useQuery({ queryKey: ["managed-collabs"], queryFn: getManagedCollaborations });
  const rows = q.data ?? [];
  const stats = managedStats(rows);
  const loading = q.isPending;

  const columns: Column<ManagedCollab>[] = [
    {
      key: "influencer", header: "Influencer", render: (c) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={c.influencer?.name ?? c.influencer?.email ?? c.id} src={c.influencer?.avatar_url} size={28} />
          <div className="min-w-0">
            <p className="truncate font-medium">{c.influencer?.name ?? "—"}</p>
            {c.influencer?.instagram_handle && <p className="truncate text-xs text-muted-foreground">@{c.influencer.instagram_handle}</p>}
          </div>
        </div>
      ),
      exportValue: (c) => c.influencer?.name ?? "",
    },
    { key: "title", header: "Titel", render: (c) => <span className="font-medium">{c.title}</span>, exportValue: (c) => c.title },
    {
      key: "status", header: "Status", render: (c) => {
        const s = MANAGED_STATUS[c.status] ?? { label: c.status, variant: "outline" as const };
        return <Badge variant={s.variant} size="sm">{s.label}</Badge>;
      }, exportValue: (c) => (MANAGED_STATUS[c.status]?.label ?? c.status),
    },
    { key: "budget", header: "Budget", align: "right", render: (c) => (c.budget != null ? m.fmt(c.budget) : "—"), exportValue: (c) => c.budget ?? "" },
    { key: "updated", header: "Bijgewerkt", align: "right", render: (c) => relativeDate(c.updated_at), exportValue: (c) => c.updated_at },
    {
      key: "manage", header: "", align: "right", render: (c) => (
        <Button render={<a href={managedCollabUrl(c.id)} />} size="xs" variant="outline" className="gap-1.5">
          Beheren <HugeiconsIcon icon={ArrowUpRight01Icon} size={16} />
        </Button>
      ), exportValue: () => "",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Collaboraties"
        icon={HandshakeIcon}
        title="Influencer-collaboraties"
        description="Live overzicht van de samenwerkingen — beheerd in het influencer-management dashboard."
        tabs={
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <HugeiconsIcon icon={LockIcon} size={14} />
            Alleen-lezen · beheren gebeurt in het management-dashboard
          </div>
        }
      />

      <FadeIn>
        {loading ? (
          <Skeleton className="h-28 rounded-2xl" />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile label="Actieve collabs" value={stats.active} unit="number" icon={HandshakeIcon} />
            <KpiTile label="Afgerond" value={stats.completed} unit="number" icon={CheckmarkCircle01Icon} />
            <KpiTile label="Totaal" value={stats.total} unit="number" icon={CheckListIcon} />
            <KpiTile label="Totaal budget" value={stats.total_budget} unit="currency" icon={EuroIcon} higherIsBetter={null} money={m.fmt} />
          </div>
        )}
      </FadeIn>

      <FadeIn delay={0.05}>
        <BreakdownTable
          title={`Alle collaboraties (${rows.length})`}
          description="Klik op “Beheren” om een samenwerking te openen in het management-dashboard."
          rows={rows}
          columns={columns}
          rowKey={(c) => c.id}
          empty={q.isError ? "Geen toegang tot de collaboraties (vraag toegang tot het management-dashboard)." : "Nog geen collaboraties."}
          exportName="Prado Pulse — Collaboraties"
        />
      </FadeIn>
    </div>
  );
}
